import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread";
/**
 * Array tools.
 *
 * @module array
 * @license Apache-2.0
 * @author drmats
 */

import { flow, identity, pipe } from "./func";
import { dec, inc, randomInt, sub } from "./math";
import { objectReduce } from "./struct";
import { isArray, isNumber } from "./type";
export var countBy = function countBy(arr) {
  var iteratee = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : identity;
  return arr.reduce(function (acc, el) {
    return function (key) {
      return isNumber(acc[key]) ? _objectSpread({}, acc, _defineProperty({}, key, acc[key] + 1)) : _objectSpread({}, acc, _defineProperty({}, key, 1));
    }(iteratee(el));
  }, Object.create(null));
};
export var draw = function draw(arr) {
  return arr[randomInt() % arr.length];
};
export var drop = function drop(n) {
  return function (arr) {
    return arr.slice(n);
  };
};
export var dropLast = function dropLast(n) {
  return function (arr) {
    return arr.slice(0, arr.length - n);
  };
};
export var findDuplicates = function findDuplicates(arr) {
  var iteratee = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : identity;
  return objectReduce(countBy(arr, iteratee), function (acc, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        k = _ref2[0],
        v = _ref2[1];

    return v > 1 ? [].concat(_toConsumableArray(acc), [k]) : acc;
  }, []);
};
export var flatten = function flatten(arr) {
  return arr.reduce(function (acc, el) {
    return [].concat(_toConsumableArray(acc), _toConsumableArray(el));
  }, []);
};
export var head = function head(arr) {
  return arr[0];
};
export var init = function init(arr) {
  return arr.slice(0, arr.length - 1);
};
export var isContinuous = function isContinuous() {
  var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var neighbour = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (a, b) {
    return b - a === 1;
  };
  return isSorted(arr, neighbour);
};
export var isSorted = function isSorted(arr) {
  var cmp = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (a, b) {
    return a <= b;
  };
  return isArray(arr) ? zip(arr, tail(arr)).every(function (pair) {
    return cmp.apply(void 0, _toConsumableArray(pair));
  }) : false;
};
export var last = function last(arr) {
  return arr[arr.length - 1];
};
export var range = function range() {
  var start = 0,
      stop = 0,
      step = 1,
      arr = [];

  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  if (args.length === 1) {
    stop = args[0];
  } else if (args.length === 2) {
    start = args[0];
    stop = args[1];
  } else if (args.length === 3) {
    start = args[0];
    stop = args[1];
    step = args[2];
    if (step === 0) throw new RangeError("array.range() 'step' argument must not be zero");
  } else throw new TypeError("array.range() expected at most 3 arguments, got ".concat(args.length));

  while (start < stop && step > 0 || start > stop && step < 0) {
    arr[arr.length] = start;
    start += step;
  }

  return arr;
};
export var removeDuplicates = flow(countBy, Object.keys.bind(Object));
export var shuffle = function shuffle(arr) {
  if (!isArray(arr)) throw new TypeError("array.shuffle() expected array as argument, got [".concat(typeof arr, "]"));

  for (var i = dec(arr.length); i > 0; i -= 1) {
    var j = randomInt() % inc(i);
    var _ref3 = [arr[j], arr[i]];
    arr[i] = _ref3[0];
    arr[j] = _ref3[1];
  }

  return arr;
};
export var sparse = function sparse() {
  var start = 0,
      stop = 0,
      size = 0,
      hash = Object.create(null),
      interval = 0;

  for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    args[_key2] = arguments[_key2];
  }

  if (args.length === 2) {
    stop = args[0];
    size = args[1];
  } else if (args.length === 3) {
    start = args[0];
    stop = args[1];
    size = args[2];
  } else throw new TypeError("array.sparse() expected 2 or 3 arguments, got ".concat(args.length));

  if (start > stop) {
    var _ref4 = [stop, start];
    start = _ref4[0];
    stop = _ref4[1];
  }

  interval = stop - start;

  if (size <= 0 || interval === 0) {
    return [];
  }

  if (size >= interval) {
    return range(start, stop);
  }

  while (size > 0) {
    var val = randomInt() % interval + start;

    if (!Object.hasOwnProperty.call(hash, val)) {
      hash[val] = val;
      size = dec(size);
    }
  }

  return Object.values(hash).sort(sub);
};
export var tail = function tail(arr) {
  return arr.slice(1);
};
export var take = function take(n) {
  return function (arr) {
    return arr.slice(0, n);
  };
};
export var takeEvery = function takeEvery(nth) {
  return function (arr) {
    return isNumber(nth) && nth > 0 && isArray(arr) ? pipe(range(Math.ceil(arr.length / nth)).reduce(function (_ref5) {
      var _ref6 = _slicedToArray(_ref5, 2),
          taken = _ref6[0],
          rest = _ref6[1];

      return [take(1)(rest).concat(taken), drop(nth)(rest)];
    }, [[], arr]))(head, function (arr) {
      return arr.reverse();
    }) : arr;
  };
};
export var takeLast = function takeLast(n) {
  return function (arr) {
    return arr.slice(arr.length - n);
  };
};
export var zipWith = function zipWith(f) {
  return function () {
    for (var _len3 = arguments.length, arrs = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      arrs[_key3] = arguments[_key3];
    }

    return range(head(arrs.map(function (arr) {
      return arr.length;
    }).sort(sub))).map(function (i) {
      return f.apply(void 0, _toConsumableArray(arrs.map(function (arr) {
        return arr[i];
      })));
    });
  };
};
export var zip = zipWith(function () {
  for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
    args[_key4] = arguments[_key4];
  }

  return args;
});